import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, concatAll, distinctUntilChanged, find, map, shareReplay, switchMap, filter } from 'rxjs/operators';
import Env from '@ridango/core/env';
import {
    Area,
    BaseFareProduct,
    CategoryProduct,
    ProductArea,
    Product,
    Preferences,
    Price,
    ActivationType,
    PeriodType,
    CategoryProductDetails,
    ActivationMode,
    LocalProduct,
    ProductTravellerType,
    ProductGroup,
    ParentProductGroup,
    BaseFareSubGroup,
    ProductCode
} from './purchase.model';
import { TranslateService } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { TravelMedia } from '@ridango/core/lib/ridango.models';
import { AppStartupService, TranslateObjectFieldPipe } from '@ridango/core';
import { CustomerAccountsFacade } from '../customer-accounts/customer-accounts.facade';
import { Temporal } from '@js-temporal/polyfill';
import { NotificationFacade } from '../notifications/+state/notification.facade';

const DEFAULT_MAX_TICKETS_ALLOWED = 50;

@Injectable({
    providedIn: 'root'
})
export class PurchaseService {
    constructor(
        private translate: TranslateService,
        @Inject('ENV') public env: Env,
        private http: HttpClient,
        private translatePipe: TranslateObjectFieldPipe,
        private customerAccountsFacade: CustomerAccountsFacade,
        private appConfig: AppStartupService,
        private notification: NotificationFacade
    ) {}

    readonly MAX_TICKETS_AMOUNT = this.env.PURCHASE?.MAX_TICKETS_ALLOWED ?? DEFAULT_MAX_TICKETS_ALLOWED;

    private activationUrl: BehaviorSubject<string> = new BehaviorSubject<string>('');
    public activationUrl$ = this.activationUrl.asObservable();

    private activationMethod: BehaviorSubject<ActivationType> = new BehaviorSubject(ActivationType.AFTER_PURCHASE);
    public activationMethod$ = this.activationMethod.asObservable();

    private isPurchaseDataLoading = new BehaviorSubject(false);
    public isPurchaseDataLoading$ = this.isPurchaseDataLoading.asObservable();

    private preferenceSource = new BehaviorSubject<Preferences>(null);
    public currentPreference = this.preferenceSource.asObservable();

    private ticketPurchaseCountSubject = new BehaviorSubject<number>(0);
    private ticketCartCountSubject = new BehaviorSubject<number>(0);
    private productTravellerTypePurchaseCountSubject = new BehaviorSubject<Record<string, number>>({});
    public productTravellerTypePurchaseCount$ = this.productTravellerTypePurchaseCountSubject.asObservable();

    private activationModeSubject = new BehaviorSubject<ActivationMode>(ActivationMode.IMMEDIATELY);
    public activationMode$ = this.activationModeSubject.asObservable();

    private selectedStartingDateSubject = new BehaviorSubject<Temporal.PlainDate | null>(null);
    public selectedStartingDate$ = this.selectedStartingDateSubject.asObservable();

    private isInDateSelectionSubject = new BehaviorSubject<boolean>(false);
    public isInDateSelection$ = this.isInDateSelectionSubject.asObservable();

    private baseFareProductsCache: Map<string, BaseFareProduct> = new Map();

    public ticketCount$: Observable<number> = combineLatest([
        this.ticketPurchaseCountSubject.asObservable(),
        this.ticketCartCountSubject.asObservable()
    ]).pipe(
        map(([localCount, cartCount]) => localCount + cartCount),
        distinctUntilChanged()
    );

    private confirmDateSelectionSubject = new Subject<void>();
    public confirmDateSelection$ = this.confirmDateSelectionSubject.asObservable();

    readonly productsLocalStorageKey = this.env.APP_NAME + '-products-to-purchase';

    private getDistributionChannel(): string {
        return this.env.IS_MOBILE_APP ? 'mobile' : 'WEB';
    }

    public choosePreference(preference: Preferences) {
        this.preferenceSource.next(preference);
    }

    public getAreas() {
        if (!this.env.WMB || !this.env.PURCHASE_FLOW.TICKET_STEPS.includes('areas')) {
            return of([]);
        }

        return this.customerAccountsFacade.selectedAccount$.pipe(
            filter((account) => account !== undefined),
            switchMap((selectedAccount) => {
                const baseUrl = `${this.env.WMB.WMB_PUBLIC_API}/v1/purchase/filter/areas/channel/${this.getDistributionChannel()}`;
                const url = new URL(baseUrl);

                if (selectedAccount) {
                    url.searchParams.set('customerAccountId', selectedAccount.id.toString());
                }

                return this.http.get<Area[]>(url.toString());
            }),
            shareReplay({ bufferSize: 1, refCount: true })
        );
    }

    public getMediums() {
        if (!this.env.WMB) {
            return of([]);
        }

        return this.customerAccountsFacade.selectedAccount$.pipe(
            distinctUntilChanged((prev, curr) => prev?.id === curr?.id),
            switchMap((selectedAccount) => {
                const baseUrl = `${this.env.WMB.WMB_PUBLIC_API}/v1/purchase/filter/mediums/channel/${this.getDistributionChannel()}`;
                const url = new URL(baseUrl);

                if (selectedAccount) {
                    url.searchParams.set('customerAccountId', selectedAccount.id.toString());
                }

                return this.http.get<Area[]>(url.toString());
            })
        );
    }

    public getProductAreas(areaLabelId?: number | string): Observable<ProductArea[]> {
        if (!this.env.WMB) {
            return of([]);
        }

        return this.customerAccountsFacade.selectedAccount$.pipe(
            distinctUntilChanged((prev, curr) => prev?.id === curr?.id),
            switchMap((selectedAccount) => {
                const baseUrl = `${this.env.WMB.WMB_PUBLIC_API}/v1/purchase/filter/fare-zones/channel/${this.getDistributionChannel()}`;
                const url = new URL(baseUrl);

                if (areaLabelId) {
                    url.searchParams.set('parentFilterId', areaLabelId.toString());
                }

                if (selectedAccount) {
                    url.searchParams.set('customerAccountId', selectedAccount.id.toString());
                }

                return this.http.get<ProductArea[]>(url.toString());
            })
        );
    }

    public getProducts(
        filterIds: string[] = [],
        travelMedia?: TravelMedia,
        travelMediaValue?: string
    ): Observable<Product[]> {
        if (!this.env.WMB) {
            return of([]);
        }

        return this.customerAccountsFacade.selectedAccount$.pipe(
            filter((account) => account !== undefined),
            switchMap((selectedAccount) => {
                const baseUrl = `${this.env.WMB.WMB_PUBLIC_API}/v1/purchase/product/channel/${this.getDistributionChannel()}`;
                const url = new URL(baseUrl);
                url.searchParams.set('filterId', filterIds.join(','));

                if (travelMedia) {
                    if (travelMedia === TravelMedia.bank_card) {
                        url.searchParams.set('travelMedia', TravelMedia.card);
                    } else {
                        url.searchParams.set('travelMedia', travelMedia);
                    }
                }

                if (travelMediaValue) {
                    if (travelMedia === TravelMedia.card || travelMedia === TravelMedia.bank_card) {
                        url.searchParams.set('cardId', travelMediaValue);
                    }

                    if (travelMedia === TravelMedia.personalCode) {
                        url.searchParams.set('personalCode', travelMediaValue);
                    }
                }

                if (selectedAccount) {
                    url.searchParams.set('customerAccountId', selectedAccount.id.toString());
                }

                return this.http.get<Product[]>(url.toString()).pipe(
                    map((products) => {
                        if (this.env.APP_NAME === 'gotland') {
                            products.forEach((product) => {
                                if (product.price?.currency === 'GEK') {
                                    product.price.currency = 'SEK';
                                }
                            });
                        }
                        return products;
                    }),
                    catchError((error) => {
                        this.setPurchaseDataLoading(false);

                        const errorMessage = error?.error?.message || error?.error?.description || error?.message;

                        this.notification?.error(this.translate.instant(errorMessage));
                        return throwError(() => error);
                    })
                );
            }),
            shareReplay({ bufferSize: 1, refCount: true })
        );
    }

    public emitSelectActivationMethod(activationMethod: ActivationType): void {
        this.activationMethod.next(activationMethod);
    }

    public emitActivationUrl(url: string): void {
        this.activationUrl.next(url);
    }

    public defineProductPriceByActivationMethod(product: Product): Price {
        if (product?.price?.amount) {
            return product.price;
        }

        if (product?.productParameters?.availableParameters.activations.length) {
            return product.productParameters?.availableParameters.activations.find(
                (el) => el.activationTrigger === this.activationMethod.getValue()
            ).price;
        }
    }

    public definedProductPrice(product: Product): Price {
        if (product?.price?.amount) {
            return product.price;
        }

        if (product?.productParameters) {
            const maxAmountProduct = product?.productParameters?.availableParameters?.activations?.reduce(
                (max, obj) => (obj.price.amount > max.price.amount ? obj : max),
                product?.productParameters?.availableParameters.activations[0]
            );

            return maxAmountProduct?.price;
        }
    }

    public getCategoryProducts(productAreaId, travelMedia?: TravelMedia): Observable<CategoryProduct[]> {
        return this.customerAccountsFacade.selectedAccount$.pipe(
            filter((account) => account !== undefined),
            switchMap((selectedAccount) => {
                const baseUrl = `${this.env.WMB.WMB_PUBLIC_API}/v1/purchase/filter/categories/channel/${this.getDistributionChannel()}`;
                const url = new URL(baseUrl);

                if (productAreaId) {
                    url.searchParams.set('parentFilterId', productAreaId.toString());
                }

                if (travelMedia) {
                    if (travelMedia === TravelMedia.bank_card) {
                        url.searchParams.set('travelMedia', TravelMedia.card);
                    } else {
                        url.searchParams.set('travelMedia', travelMedia);
                    }
                }

                if (this.env.IS_NEW_AFC && selectedAccount && selectedAccount.type.code === 'CORPORATE') {
                    url.searchParams.set('customerAccountId', selectedAccount.id.toString());
                }

                return this.http.get<CategoryProduct[]>(url.toString()).pipe(
                    map((categoryProducts) => {
                        if (this.env.APP_NAME === 'gotland') {
                            categoryProducts.forEach((categoryProduct) => {
                                categoryProduct.products?.forEach((product) => {
                                    if (product.price?.currency === 'GEK') {
                                        product.price.currency = 'SEK';
                                    }
                                });
                            });
                        }
                        return categoryProducts;
                    })
                );
            }),
            shareReplay({ bufferSize: 1, refCount: true })
        );
    }

    public getJourneyPurchaseProducts(
        priceZoneList: string,
        distributionChannels = 'mobile'
    ): Observable<CategoryProduct[]> {
        return this.customerAccountsFacade.selectedAccount$.pipe(
            switchMap((selectedAccount) => {
                const zones = priceZoneList.split(',');
                const [fromZone, ...middleZones] = zones;
                const toZone = middleZones.length > 0 ? [...middleZones].pop() : fromZone;

                const travelledZones = zones.length > 1 ? middleZones.map((code) => ({ code })) : [{ code: fromZone }];

                // New AFC don't include fromZone logic like old AFC.
                if (this.env.IS_NEW_AFC) {
                    if (!travelledZones.some((zone) => zone.code === fromZone)) {
                        travelledZones.push({ code: fromZone });
                    }
                }

                const requestBody = {
                    legs: [
                        {
                            fromZone: {
                                code: fromZone
                            },
                            toZone: {
                                code: toZone
                            },
                            travelledZones
                        }
                    ]
                };

                const baseUrl = `${this.env.WMB.WMB_PUBLIC_API}/v1/purchase/journey/product/channel/${distributionChannels}`;
                const url = new URL(baseUrl);
                url.searchParams.set('priceZoneList', priceZoneList);

                if (this.env.IS_NEW_AFC && selectedAccount && selectedAccount.type.code === 'CORPORATE') {
                    url.searchParams.set('customerAccountId', selectedAccount.id.toString());
                }

                return this.http.post<CategoryProduct[]>(url.toString(), requestBody).pipe(
                    map((categoryProducts) => {
                        if (this.env.APP_NAME === 'gotland') {
                            categoryProducts.forEach((categoryProduct) => {
                                categoryProduct.products?.forEach((product) => {
                                    if (product.price?.currency === 'GEK') {
                                        product.price.currency = 'SEK';
                                    }
                                });
                            });
                        }
                        return categoryProducts;
                    })
                );
            })
        );
    }

    public getStepsDataIds() {
        const result = {};
        let currentKey = null;

        // active route doesn't work
        window.location.pathname.split('/').forEach((segment) => {
            if (this.env.PURCHASE_FLOW?.TICKET_STEPS.includes(segment)) {
                currentKey = segment;
                result[currentKey] = [];
            } else if (currentKey) {
                result[currentKey].push(segment);
            }
        });

        return result;
    }

    public updateCartTicketCount(ticketsInCart: number): void {
        this.ticketCartCountSubject.next(ticketsInCart);
    }

    updateCounters(): void {
        let localProductsCount = 0;
        let localProductTicketTypeCounts = {};
        const localProducts = this.getLocalProducts();

        for (const product of localProducts) {
            localProductsCount += product.amount;

            if (product.isSingleTicketGroup) {
                localProductTicketTypeCounts = product.dynamicProductParameters;
            }
        }
        this.productTravellerTypePurchaseCountSubject.next({ ...localProductTicketTypeCounts });
        this.ticketPurchaseCountSubject.next(localProductsCount);
    }

    public reduceLocalTicketTypeAmount(product: Product) {
        const productId = (product.id || product.productId) as string;

        if (!productId) {
            console.error('Cannot reduce ticket: no product ID found', product);
            return;
        }

        const productIdWithoutParameters = productId.split(':')[0];

        if (product.isSingleTicketGroup) {
            return this.reduceLocalTicketTypeAsParameter(product);
        } else {
            return this.reduceLocalTicketTypeAsProduct(productIdWithoutParameters);
        }
    }

    public reduceLocalTicketTypeAsParameter(productTravellerType: ProductTravellerType) {
        const productIdWithoutParameters = (productTravellerType.id as string).split(':')[0];
        const localProducts = JSON.parse(localStorage.getItem(this.productsLocalStorageKey)) || [];
        const localProduct = localProducts.find((product) => product.id == productIdWithoutParameters);
        if (!localProduct) {
            return;
        }
        const ticketGroupParameterCode = productTravellerType.productTravellerTypeParameterCode;
        const localProductParameterValue = localProduct.dynamicProductParameters[ticketGroupParameterCode];
        if (!localProductParameterValue) {
            return;
        }
        localProduct.dynamicProductParameters[ticketGroupParameterCode]--;
        localStorage.setItem(this.productsLocalStorageKey, JSON.stringify(localProducts));
        this.updateCounters();
    }

    public reduceLocalTicketTypeAsProduct(productId) {
        const localProducts = JSON.parse(localStorage.getItem(this.productsLocalStorageKey)) || [];
        const localProduct = localProducts.find((product) => product.id == productId);

        if (!localProduct) {
            return;
        }

        if (localProduct.amount === 1) {
            const filteredProducts = localProducts.filter((p) => p.id != productId);
            localStorage.setItem(this.productsLocalStorageKey, JSON.stringify(filteredProducts));
        } else {
            localProduct.amount--;
            localStorage.setItem(this.productsLocalStorageKey, JSON.stringify(localProducts));
        }
        this.updateCounters();
    }

    public getLocalProducts(): LocalProduct[] {
        return JSON.parse(localStorage.getItem(this.productsLocalStorageKey)) || [];
    }

    public getLocalProductIndexByIdAndStartZone(productId: string, startZoneId: number): number | undefined {
        const localProducts = JSON.parse(localStorage.getItem(this.productsLocalStorageKey)) || [];
        const localProductIndex = localProducts.findIndex((p) => p.id === productId && p.startZoneId === startZoneId);
        return localProductIndex >= 0 ? localProductIndex : undefined;
    }

    public addFirstLocalProduct(product: Product) {
        const localProducts = this.getLocalProducts();
        const productToStore = {
            ...product,
            dynamicProductParameters: product.dynamicProductParameters || {}
        };
        localStorage.setItem(this.productsLocalStorageKey, JSON.stringify([...localProducts, productToStore]));
        this.updateCounters();
    }

    public increaseLocalProductTravellerTypeAmount(product: ProductTravellerType) {
        const isSingleTicketGroup = product.isSingleTicketGroup;
        const isDynamicLength = this.isDynamicLengthProduct(product);

        if (isDynamicLength) {
            return this.addFirstLocalProduct(product);
        }

        const localProductIndex = this.getLocalProductIndexByIdAndStartZone(product.id as string, product.startZoneId);
        if (localProductIndex === undefined) {
            if (isSingleTicketGroup) {
                product.dynamicProductParameters = {
                    [product.productTravellerTypeParameterCode]: 1
                };
                this.updateCounters();
            }
            return this.addFirstLocalProduct(product);
        }

        if (isSingleTicketGroup) {
            return this.increaseLocalTicketTypeAsParameter(product, localProductIndex);
        } else {
            return this.increaseLocalTicketTypeAsProduct(localProductIndex);
        }
    }

    public increaseLocalTicketTypeAsProduct(localProductIndex: number) {
        const localProducts = this.getLocalProducts();
        localProducts[localProductIndex].amount++;
        localStorage.setItem(this.productsLocalStorageKey, JSON.stringify(localProducts));
        this.updateCounters();
    }

    public increaseLocalTicketTypeAsParameter(product: ProductTravellerType, localProductIndex: number) {
        const localProducts = this.getLocalProducts();
        const localProduct = localProducts[localProductIndex];
        const productTravellerTypeParameterCode = product.productTravellerTypeParameterCode;
        const localProductParameterValue = localProduct.dynamicProductParameters[productTravellerTypeParameterCode];

        if (localProductParameterValue) {
            localProduct.dynamicProductParameters[productTravellerTypeParameterCode]++;
        } else {
            localProduct.dynamicProductParameters[productTravellerTypeParameterCode] = 1;
        }

        localStorage.setItem(this.productsLocalStorageKey, JSON.stringify(localProducts));
        this.updateCounters();
    }

    public getLocalProductAmount() {
        // FIXME: actually returns all products in Local Storage, not just amount
        let localProducts;
        try {
            localProducts = JSON.parse(localStorage.getItem(this.productsLocalStorageKey)) || [];
        } catch (e) {
            localProducts = [];
        }

        return localProducts;
    }

    public setArrayLocalProducts(products: Product[]) {
        localStorage.setItem(this.productsLocalStorageKey, JSON.stringify(products));
        this.updateCounters();
        return products;
    }

    public resetProductsLocalStorage() {
        localStorage.setItem(this.productsLocalStorageKey, JSON.stringify([]));
        this.updateCounters();
    }

    public getCategoryProductById(id: string, productAreaId: string) {
        return this.getCategoryProducts(productAreaId).pipe(
            concatAll(),
            find((product) => product.id.toString() === id)
        );
    }

    public getStepDataIdsByStepName(step: string) {
        return this.getStepsDataIds()[step] || [];
    }

    public setPurchaseDataLoading(isLoading: boolean): void {
        queueMicrotask(() => this.isPurchaseDataLoading.next(isLoading));
    }

    public getCategoryProductDetails() {
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/categories/product/detail`;

        return this.http.get<CategoryProductDetails[]>(url);
    }

    public fetchPeriodTypes(productAreaId?: string): Observable<PeriodType[]> {
        return this.customerAccountsFacade.selectedAccount$.pipe(
            switchMap((selectedAccount) => {
                const filterIds = [];
                if (productAreaId) {
                    filterIds.push(productAreaId);
                }

                const baseUrl = `${this.env.WMB.WMB_PUBLIC_API}/v1/purchase/filter/period/channel/${this.getDistributionChannel()}`;
                const url = new URL(baseUrl);

                if (filterIds.length > 0) {
                    url.searchParams.set('filterId', filterIds.join(','));
                }

                if (selectedAccount) {
                    url.searchParams.set('customerAccountId', selectedAccount.id.toString());
                }

                return this.http.get<PeriodType[]>(url.toString());
            })
        );
    }

    public getProductDescription(productAreas, product) {
        const productArea = productAreas?.find(
            (pa) => pa.startZoneId === product.startZoneId && pa.endZoneId === product.endZoneId
        );

        const alternativeDescription =
            this.env.PURCHASE?.ALTERNATIVE_PRODUCT_DESCRIPTION_TRANSLATION?.[
                this.translatePipe.transform(productArea?.name, null, 'en')
            ]?.[this.translatePipe.transform(product.description, null, 'en')];

        return alternativeDescription
            ? this.translate.instant(alternativeDescription)
            : this.translatePipe.transform(product?.description, null, this.translate.currentLang);
    }

    get getProductAreasLocalStorageKey(): Product[] | null {
        try {
            return JSON.parse(localStorage.getItem(this.productsLocalStorageKey));
        } catch (e) {
            return null;
        }
    }

    public setActivationMode(mode: ActivationMode): void {
        this.activationModeSubject.next(mode);
        if (mode === ActivationMode.IMMEDIATELY) {
            this.selectedStartingDateSubject.next(null);
        }
    }

    public setStartingDate(date: Temporal.PlainDate | null): void {
        this.selectedStartingDateSubject.next(date);
    }

    public getCurrentActivationMode(): ActivationMode {
        return this.activationModeSubject.value;
    }

    public getCurrentStartingDate(): Temporal.PlainDate | null {
        return this.selectedStartingDateSubject.value;
    }

    public setDateSelectionMode(isInDateSelection: boolean): void {
        this.isInDateSelectionSubject.next(isInDateSelection);
    }

    public isInDateSelectionMode(): boolean {
        return this.isInDateSelectionSubject.value;
    }

    public triggerDateSelectionConfirmation(): void {
        this.confirmDateSelectionSubject.next();
    }

    public createParentProductGroups(productGroups: ProductGroup[]): ParentProductGroup[] {
        const { singleGroups, nonSingleGroups } = this.splitGroups(productGroups);

        const parentGroups: ParentProductGroup[] = [];

        const singleParentGroup = this.createSingleParentGroup(singleGroups);

        if (singleParentGroup) {
            parentGroups.push(singleParentGroup);
        }

        parentGroups.push(...nonSingleGroups.map((group) => this.createNonSingleParentGroup(group)));

        return parentGroups;
    }

    private splitGroups(productGroups: ProductGroup[]): {
        singleGroups: ProductGroup[];
        nonSingleGroups: ProductGroup[];
    } {
        const singleGroups: ProductGroup[] = [];
        const nonSingleGroups: ProductGroup[] = [];

        productGroups.forEach((group) => {
            const isSingle = group.code?.toString().includes('SINGLE') && !group.code.toString().includes('GROUP');

            if (isSingle) {
                singleGroups.push(group);
            } else {
                nonSingleGroups.push(group);
            }
        });

        return { singleGroups, nonSingleGroups };
    }
    private createSingleParentGroup(singleGroups: ProductGroup[]): ParentProductGroup | null {
        if (!singleGroups.length) {
            return null;
        }

        return {
            name: this.translate.instant('CATEGORY_PRODUCT_SINGLE_TICKET'),
            code: 'SINGLE_GROUP',
            groups: singleGroups,
            isParentGroup: true,
            baseFareSubGroups: this.createBaseFareSubGroups(singleGroups)
        };
    }
    private createNonSingleParentGroup(group: ProductGroup): ParentProductGroup {
        return {
            name: this.getGroupDisplayName(group.code),
            code: group.code.toString(),
            groups: [group],
            isParentGroup: false,
            baseFareSubGroups: this.createBaseFareSubGroups([group])
        };
    }
    private getBaseFareDisplayName(product: Product): string {
        const currentLang = this.translate.currentLang;

        if (product.baseFareProduct?.name?.translations) {
            const translation = product.baseFareProduct.name.translations.find((t) => t.lang === currentLang);

            if (translation) {
                return translation.translation;
            }

            if (product.baseFareProduct.name.translations.length > 0) {
                return product.baseFareProduct.name.translations[0].translation;
            }
        }

        return product.baseFareProduct?.code || 'Unknown';
    }
    private calculateMaxPrice(products: Product[]): Price | null {
        let maxPrice: Price | null = null;

        products.forEach((product) => {
            if (!product.price) {
                return;
            }

            const priceAmount = product.price.amount ?? product.price.maxAmount;

            if (
                priceAmount !== null &&
                priceAmount !== undefined &&
                (!maxPrice || priceAmount > (maxPrice.amount ?? maxPrice.maxAmount ?? 0))
            ) {
                maxPrice = product.price;
            }
        });

        return maxPrice;
    }
    private getGroupDisplayName(code: ProductCode): string {
        return this.translate.instant(`CATEGORY_PRODUCT_${code}`);
    }
    private createBaseFareSubGroups(groups: ProductGroup[]): BaseFareSubGroup[] {
        const { baseFareMap, productsWithoutBaseFare } = this.collectProducts(groups);

        return [
            ...this.createBaseFareGroups(baseFareMap),
            ...this.createGroupsWithoutBaseFare(productsWithoutBaseFare)
        ].sort((a, b) => a.baseFareName.localeCompare(b.baseFareName));
    }
    private collectProducts(groups: ProductGroup[]): {
        baseFareMap: Map<string, Product[]>;
        productsWithoutBaseFare: Product[];
    } {
        const baseFareMap = new Map<string, Product[]>();
        const productsWithoutBaseFare: Product[] = [];

        groups.forEach((group) => {
            group.products.forEach((category) => {
                category.products?.forEach((product) => {
                    if (product.baseFareProduct) {
                        const code = product.baseFareProduct.code;

                        if (!baseFareMap.has(code)) {
                            baseFareMap.set(code, []);
                        }

                        const productsInMap = baseFareMap.get(code);

                        if (productsInMap) {
                            productsInMap.push(product);
                        } else {
                            baseFareMap.set(code, [product]);
                        }
                    } else {
                        productsWithoutBaseFare.push(product);
                    }
                });
            });
        });

        return {
            baseFareMap,
            productsWithoutBaseFare
        };
    }
    private createBaseFareGroups(baseFareMap: Map<string, Product[]>): BaseFareSubGroup[] {
        const groups: BaseFareSubGroup[] = [];

        baseFareMap.forEach((products, baseFareCode) => {
            const firstProduct = products[0];

            if (!firstProduct) {
                return;
            }

            if (firstProduct.baseFareProduct) {
                this.baseFareProductsCache.set(baseFareCode, firstProduct.baseFareProduct);
            }

            groups.push({
                baseFareCode,
                baseFareName: this.getBaseFareDisplayName(firstProduct),
                products: this.sortProducts(products),
                maxPrice: this.calculateMaxPrice(products)
            });
        });

        return groups;
    }
    private createGroupsWithoutBaseFare(productsWithoutBaseFare: Product[]): BaseFareSubGroup[] {
        const productNameMap = new Map<string, Product[]>();

        productsWithoutBaseFare.forEach((product) => {
            const productName = product.name?.translations?.[0]?.translation ?? String(product.id) ?? 'Unknown';

            const groupKey = productName.split(' ')[0];

            if (!productNameMap.has(groupKey)) {
                productNameMap.set(groupKey, []);
            }

            const productsInMap = productNameMap.get(groupKey);

            if (productsInMap) {
                productsInMap.push(product);
            } else {
                productNameMap.set(groupKey, [product]);
            }
        });

        const groups: BaseFareSubGroup[] = [];

        productNameMap.forEach((products, groupKey) => {
            groups.push({
                baseFareCode: groupKey,
                baseFareName: groupKey,
                products: this.sortProducts(products),
                maxPrice: this.calculateMaxPrice(products)
            });
        });

        return groups;
    }
    private sortProducts(products: Product[]): Product[] {
        return [...products].sort((a, b) => {
            const aProfile = a.userProfiles?.[0]?.name?.translations?.[0]?.translation ?? '';

            const bProfile = b.userProfiles?.[0]?.name?.translations?.[0]?.translation ?? '';

            return aProfile.localeCompare(bProfile);
        });
    }

    public getBaseFareProduct(baseFareCode: string): BaseFareProduct | null {
        return this.baseFareProductsCache.get(baseFareCode) || null;
    }

    public isDynamicLengthProduct(product: CategoryProduct | Product): boolean {
        if ('products' in product && Array.isArray(product.products)) {
            return product.products.some((p) => p.hasDynamicLength === true);
        }

        return 'hasDynamicLength' in product && product.hasDynamicLength === true;
    }

    getPrePurchaseCheckActivationDateParams(selectedActivationMethod: string): { date: string; isToday: boolean } {
        let prePurchaseCheckActivationDate = Temporal.Now.zonedDateTimeISO(this.appConfig.getTimezone())
            .toInstant()
            .toString();
        let specifiedActivationDateIsToday = false;

        if (this.env.ENABLE_CHOOSE_STARTING_DATE) {
            const currentSelectedDate = this.getStepDataIdsByStepName('ticket-activation')[1];

            if (selectedActivationMethod === 'SPECIFIED_ACTIVATION_DATE' && currentSelectedDate) {
                const today = Temporal.Now.plainDateISO(this.appConfig.getTimezone());
                const currentSelectedDatePlain = Temporal.PlainDate.from(currentSelectedDate);

                if (currentSelectedDatePlain.equals(today)) {
                    specifiedActivationDateIsToday = true;
                } else {
                    // Convert the selected PlainDate into a full date-time at midnight. PlainDate has no time,
                    // so we must specify an exact time (00:00) before converting it to a ZonedDateTime.
                    // This creates a concrete moment at the start of the selected day in the user's timezone.
                    const zonedDateTime = currentSelectedDatePlain
                        .toPlainDateTime({ hour: 0, minute: 0, second: 0 })
                        .toZonedDateTime(this.appConfig.getTimezone());

                    prePurchaseCheckActivationDate = zonedDateTime.toInstant().toString();
                }
            }
        }

        return { date: prePurchaseCheckActivationDate, isToday: specifiedActivationDateIsToday };
    }
}
